import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Camera } from 'lucide-react'
import { publicApiClient } from '@/api/public'

const DEFAULT_NOTICE =
  'We keep a photo of you so that your trainer can confirm you are the person who attended your course, and so that anyone ' +
  'checking your training certificate can confirm it is yours. It is stored securely and you can ask us to remove it at any time.'

/** A training company's photo privacy notice, for the people whose photos they hold. Public; no sign-in. */
export default function PhotoNoticePage() {
  const { slug } = useParams<{ slug: string }>()

  const { data, isLoading } = useQuery({
    queryKey: ['public-photo-notice', slug],
    queryFn: () => publicApiClient.getPhotoNotice(slug!),
    enabled: !!slug,
    retry: false,
  })

  const colour = data?.primaryColour ?? '#4F46E5'

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-2xl mx-auto px-5 py-10">
        {isLoading && <p className="text-sm text-gray-400">Loading…</p>}

        {!isLoading && !data && (
          <p className="text-sm text-gray-600">We couldn't find that organisation.</p>
        )}

        {data && (
          <>
            <div className="flex items-center gap-3 mb-6">
              {data.logoUrl && <img src={data.logoUrl} alt="" className="h-10 w-auto object-contain" />}
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${colour}18` }}>
                <Camera size={18} style={{ color: colour }} />
              </div>
              <h1 className="text-xl font-bold text-gray-900">{data.tenantName}: how we use your photo</h1>
            </div>

            {!data.enabled ? (
              <p className="text-sm text-gray-700 leading-relaxed">
                {data.tenantName} does not currently use identity photos.
              </p>
            ) : (
              <div className="space-y-6 text-sm text-gray-700 leading-relaxed">
                <p className="whitespace-pre-line">{data.notice?.trim() || DEFAULT_NOTICE}</p>

                <div>
                  <h2 className="font-semibold text-gray-900 mb-1.5">Who can see your photo</h2>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>The administrators at {data.tenantName}.</li>
                    <li>The trainers running the courses you attend, around the day of the course.</li>
                    <li>If you work for a company that arranged your training, that company's authorised managers.</li>
                    <li>You, when you sign in to your own page.</li>
                    {data.showOnVerification && (
                      <li>Anyone who scans the QR code on your certificate or opens its verification link, while the certificate is current.</li>
                    )}
                  </ul>
                </div>

                <div>
                  <h2 className="font-semibold text-gray-900 mb-1.5">Your choices</h2>
                  <p>
                    You can ask to see your photo, to have it replaced, or to have it deleted at any time
                    {data.contactEmail ? <>, by contacting <a href={`mailto:${data.contactEmail}`} className="text-indigo-600 hover:underline">{data.contactEmail}</a></> : ', by contacting your training provider'}.
                    If you are unhappy with how your photo is handled you can also complain to the Information Commissioner's Office (ico.org.uk).
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
